import { DatePicker } from "@mimber/ui-web"
import { FormItem } from "react-hook-form-antd"
import {
  useFormContext,
  FieldValues,
  FieldPath,
  Controller,
} from "react-hook-form"
import dayjs, { Dayjs } from "dayjs"
import type { DatePickerFieldProps } from "./DatePickerField.types"

/**
 * DatePickerField component that integrates React Hook Form with Ant Design DatePicker
 * Uses react-hook-form-antd's FormItem for seamless integration
 * Note: Uses Controller for transformation between ISO string (form) and Dayjs (antd)
 * The form value is an ISO string (or null), not a Date object.
 */
export function DatePickerField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  control,
  label,
  required,
  help,
  placeholder,
  disabled,
  tooltip,
  showTime = false,
  format,
}: DatePickerFieldProps<TFieldValues, TName>) {
  const { control: contextControl } = useFormContext<TFieldValues>()
  const formControl = control || contextControl
  const displayFormat = format ?? (showTime ? "YYYY-MM-DD HH:mm" : "YYYY-MM-DD")

  return (
    <FormItem
      control={formControl}
      name={name}
      label={label}
      required={required}
      help={help}
      tooltip={tooltip}
    >
      <Controller
        name={name}
        control={formControl}
        render={({ field }) => (
          <DatePicker
            value={field.value ? dayjs(field.value) : null}
            onChange={(date: Dayjs | null) => {
              field.onChange(date ? date.toISOString() : null)
            }}
            placeholder={placeholder}
            disabled={disabled}
            format={displayFormat}
            showTime={showTime}
            style={{ width: "100%" }}
          />
        )}
      />
    </FormItem>
  )
}
